import { formatDistanceToNowStrict } from 'date-fns'
import { vi } from 'date-fns/locale'
import { MoreHorizontal } from 'lucide-react'
import * as React from 'react'

import { cn } from '@/lib/utils'
import { useScrollHighlight } from '@/hooks/use-scroll-highlight'
import { toast } from '@/hooks/use-toast'

import { UserAvatarPopover } from '@/components/common/modal/avatar-hover'
import MarkdownEditor from '@/components/markdown/markdown-editor'
import MarkdownPreview from '@/components/markdown/markdown-preview'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import CommentReactions from '@/containers/topics/comments/comment-reaction'

import { useAuth } from '@/contexts/auth-context'
import commentService from '@/services/comment.service'

import { CommentReaction, CommentType, EmojiData, ReactionType } from '@/types/comment.type'
import { UserProfile } from '@/types/user.type'

interface CommentBlogCardProps {
  comment: CommentType
  isLast: boolean
  blogSlug: string
  depth?: number
  onReply?: (commentId: number) => void
  onCollapse?: (commentId: number) => void
  isCollapsed?: boolean
  isFirstChild?: boolean
  hasChildren?: boolean
  onCommentDeleted?: (commentId: number) => void

  // EDIT support
  isEditOpen?: boolean
  editingContent?: string
  onChangeEditContent?: (v: string) => void
  onSaveEdit?: () => void
  onCancelEdit?: () => void
  onStartEdit?: (id: number, initialContent: string) => void

  // reaction props (optional, since container now passes them)
  reactions?: CommentReaction[]
  onReact?: (commentId: number, reactionType: ReactionType, emojiData: EmojiData) => void
  currentUser?: UserProfile
}

function CommentBlogCard({
  comment,
  isLast,
  depth = 0,
  onReply,
  onCollapse,
  isCollapsed = false,
  isFirstChild = false,
  hasChildren = false,
  onCommentDeleted,
  reactions = [],
  onReact,
  currentUser,
  // EDIT props
  isEditOpen,
  editingContent,
  onChangeEditContent,
  onSaveEdit,
  onCancelEdit,
  onStartEdit,
}: CommentBlogCardProps) {
  const { isHighlighted } = useScrollHighlight({
    elementIdPrefix: 'comment',
    highlightDuration: 2500,
  })

  return (
    <li
      tabIndex={0}
      id={`comment-${comment.id}`}
      className={cn(
        'group relative flex gap-0 rounded-lg bg-card p-4 mb-3 border border-border focus-within:border-primary hover:border-primary outline-none shadow-sm z-20',
        {
          'after:absolute after:bottom-[-24px] after:left-[32px] after:transform after:-translate-x-1/2 after:w-0.5 after:h-6 after:bg-border after:z-10':
            !isLast && isCollapsed,
          'ml-2 sm:ml-6 md:ml-8': depth > 0,
          'border-primary': isHighlighted(comment.id),
        }
      )}
    >
      {depth > 0 && (
        <div
          className={cn(
            'absolute -left-3 sm:-left-5 md:-left-6 top-4 w-3 sm:w-5 md:w-6  border-border rounded-bl-xl',
            {
              'border-l-2 border-b-2 h-[3.5rem] mt-[-2rem]': isFirstChild,
            }
          )}
        />
      )}

      <div className="mr-4 hidden sm:block">
        <UserAvatarPopover user={comment.user} />
      </div>

      <div className="flex flex-col w-full overflow-hidden">
        <div className="flex items-start gap-2 mb-2">
          <div className="flex items-center gap-2">
            <div className="sm:hidden block">
              <UserAvatarPopover user={comment.user} />
            </div>
            <div className="flex flex-col sm:items-center sm:flex-row gap-2">
              <span className="font-bold text-foreground">
                {comment.user?.firstName} {comment.user?.lastName}
              </span>
              <span className="text-xs text-muted-foreground">
                <span className="hidden sm:inline">commented </span>
                <a
                  href={`#comment-${comment.id}`}
                  className="underline decoration-dotted hover:decoration-solid hover:text-foreground transition"
                >
                  {formatDistanceToNowStrict(new Date(comment.createdAt), { addSuffix: true, locale: vi })}
                </a>
              </span>
            </div>
          </div>
          {/* {isAuthor && (
            <span
              className="ml-2 px-2 py-0.5 rounded-full border border-primary/20 bg-primary/10 text-xs text-primary font-semibold"
              style={{ fontSize: '11px', borderRadius: '10px' }}
            >
              Author
            </span>
          )} */}

          <CommentOptions
            comment={comment}
            onReply={onReply}
            depth={depth}
            onCommentDeleted={onCommentDeleted}
            onStartEdit={onStartEdit}
          />
        </div>

        {/* Content */}
        <div className="mb-2">
          {isEditOpen ? (
            <div className="bg-muted rounded-lg border p-3">
              <MarkdownEditor
                id={`edit-editor-${comment.id}`}
                value={editingContent ?? ''}
                onChangeContent={v => onChangeEditContent?.(v ?? '')}
                className="min-h-[100px]"
                view={{ html: false, md: true, menu: true }}
              />
              <div className="flex justify-end gap-2 mt-2">
                <Button variant="outline" size="sm" onClick={onCancelEdit}>
                  Cancel
                </Button>
                <Button size="sm" onClick={onSaveEdit} disabled={!editingContent?.trim()}>
                  Save
                </Button>
              </div>
            </div>
          ) : (
            <div className="prose prose-sm w-full overflow-hidden max-w-none">
              <MarkdownPreview>{comment.content || 'Comment'}</MarkdownPreview>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center gap-2 mt-2">
          {/* Reaction component */}
          {onReact && currentUser && (
            <CommentReactions
              commentId={comment.id}
              reactions={reactions}
              onReact={onReact}
              currentUser={currentUser}
              classNameReactionText="hidden"
            />
          )}

          {/* Toggle Collapsed button - only show if has children */}
          {hasChildren && onCollapse && (
            <button
              className="flex items-center gap-1 text-muted-foreground hover:text-foreground text-sm transition-colors"
              onClick={() => onCollapse(comment.id)}
              type="button"
            >
              {isCollapsed ? '▼' : '▲'} {comment.children?.length} replies
            </button>
          )}
        </div>
      </div>
    </li>
  )
}

interface CommentOptionsProps {
  comment: CommentType
  onReply?: (commentId: number) => void
  depth: number
  onCommentDeleted?: (commentId: number) => void
  onStartEdit?: (id: number, initialContent: string) => void
}

function CommentOptions({ comment, onReply, depth, onCommentDeleted, onStartEdit }: CommentOptionsProps) {
  const { user } = useAuth()
  const isAuthor = user?.id === comment.user?.id
  const MAX_LEVEL_COMMENT = 2

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false)
  const [isDeleting, setIsDeleting] = React.useState(false)

  const handleMenuAction = (action: string) => {
    if (action === 'copy-link') {
      const url = new URL(window.location.href)
      url.searchParams.set('commentId', comment.id.toString())
      url.searchParams.set('scrollToComments', 'true')
      navigator.clipboard.writeText(url.toString())
      toast({
        title: 'Link copied',
        description: 'Comment link has been copied to clipboard',
      })
    } else if (action === 'quote-reply') {
      if (onReply && depth < MAX_LEVEL_COMMENT) {
        onReply(comment.id)
      }
    } else if (action === 'edit') {
      onStartEdit?.(comment.id, comment.content)
    } else if (action === 'delete') {
      setIsDeleteDialogOpen(true)
    }
  }

  const handleDeleteComment = async () => {
    if (!comment.id) return

    setIsDeleting(true)
    try {
      await commentService.deleteComment(comment.id)

      toast({
        title: 'Comment deleted',
        description: 'Your comment has been successfully deleted',
      })

      setIsDeleteDialogOpen(false)

      if (onCommentDeleted) {
        onCommentDeleted(comment.id)
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to delete comment:', error)
      toast({
        title: 'Error',
        description: 'Failed to delete comment. Please try again.',
        variant: 'destructive',
      })
    } finally {
      setIsDeleting(false)
    }
  }

  const menuItems = [
    { label: 'Copy link', action: 'copy-link' },
    ...(user && depth < MAX_LEVEL_COMMENT - 1 ? [{ label: 'Quote reply', action: 'quote-reply' }] : []),
    ...(isAuthor ? [{ label: 'Edit', action: 'edit' }] : []),
    ...(isAuthor ? [{ label: 'Delete', action: 'delete', danger: true }] : []),
  ]

  return (
    <>
      <Popover>
        <PopoverTrigger asChild>
          <button
            className="ml-auto w-5 h-5 flex items-center justify-center rounded hover:bg-muted transition"
            aria-label="Open menu"
            tabIndex={0}
          >
            <MoreHorizontal className="text-muted-foreground group-hover:text-foreground transition" size={20} />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-60 p-0" align="end" sideOffset={4} hidden={menuItems.length === 0}>
          <div className="py-1">
            {menuItems.map(item => (
              <button
                key={item.action}
                className={`w-full text-left px-4 h-8 flex items-center text-sm hover:bg-muted transition ${
                  item.danger ? 'text-destructive' : 'text-foreground'
                }`}
                style={{ fontSize: '14px' }}
                onClick={() => handleMenuAction(item.action)}
              >
                {item.label}
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>

      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Comment</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this comment? This action cannot be undone and will also delete all
              replies to this comment.
            </DialogDescription>
          </DialogHeader>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)} disabled={isDeleting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteComment} disabled={isDeleting}>
              {isDeleting ? 'Deleting...' : 'Delete Comment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default CommentBlogCard
